/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines-per-function */
import { EventMessage, EventPayloadCrud } from '@rta/rta-blueprint-library';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { Kitchen3dController } from './controller';
import { Render3DKitchen } from './core/components';
import { BrightnessPanel } from './core/components/render-3d-kitchen/brightness-panel';
import { ToolsPanel } from './core/components/tools-panel/tools-panel';

// Export texture options for consumers
export { SKY_TEXTURES, FLOOR_TEXTURES } from './core/constants/texture-options';
export type { TextureOption } from './core/constants/texture-options';

export type Kitchen3dProps = {
	backgroundColor?: string;
	controller?: Kitchen3dController;
	/** Optional render offset to apply as focal offset. Accepts CSS-like units:
	 * e.g., { x: '100px', y: '10%' }. Suffix must be 'px' or '%'.
	 * Numbers are treated as pixel values. */
	renderOffset?: { x: string | number; y: string | number };
	/** Optional viewport anchor to position the scene within the viewport.
	 * Values are 0-1 where 0.5 = center (default).
	 * Example: { vertical: 0.33 } positions the scene in the bottom third. */
	viewportAnchor?: { vertical?: number; horizontal?: number };
	/** FOV used when capturing the static image via setImage(). Default: 30 */
	imageFov?: number;
	/** Optional floor for the canvas height in px. Default: none — the canvas
	 * fills its container. A container shorter than a floor has the canvas
	 * overflow and get cropped (the framing is centred on a canvas the viewer
	 * only partly sees), so set one only when that is what you want. */
	canvasMinHeight?: number;
	/**
	 * Receives this instance's engine adapter when ready, and `null` on unmount. Capture through it
	 * to guarantee the image comes from the engine the user is actually looking at.
	 */
	instanceRef?: (instance: unknown | null) => void;
	/**
	 * Whether this instance claims the controller's single engine slot. Default true. Preview
	 * instances pass false when another viewport already owns the controller.
	 */
	ownsController?: boolean;
	/** Whether the viewer offers AR. Default: true. On a device that supports
	 * AR the viewer shows its own AR button; a consumer that gates AR (e.g.
	 * until the customer has saved their design) passes false to hide it and
	 * skip pre-warming the AR modules. */
	arEnabled?: boolean;
};

export const Kitchen3d = (props: Kitchen3dProps): ReactElement => {
	const {
		backgroundColor = 'white',
		controller = null,
		renderOffset = null,
		viewportAnchor = null,
		imageFov = null,
		canvasMinHeight,
		instanceRef = null,
		ownsController = true,
		arEnabled = true
	} = props;

	const [toolsPanelVisible, setToolsPanelVisible] = useState(false);
	const [showControlsPanel, setShowControlsPanel] = useState(false);
	// Debug state drives the tools-icon visibility. It MUST live in React state:
	// set_debug_mode mutates controller.options in place, which never re-renders
	// this component, so reading controller.options.debugMode directly in JSX left
	// the 🔧 icon stuck on its mount-time default (isDevelopmentMode()) instead of
	// tracking the toggle. Seed from the controller, then follow debug_mode events.
	const [debugUi, setDebugUi] = useState<{ debugMode: boolean; showTools: boolean }>(() => ({
		debugMode: controller?.options?.debugMode ?? false,
		showTools: controller?.options?.debugOptions?.showTools ?? false
	}));

	// const [_forceRender, setForceRender] = useState<number>(0);
	const [controllerSubscriptions, setControllerSubscriptions] = useState<string[]>([]);
	const [event, setEvent] = useState<EventMessage>(null);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_textureEvent, setTextureEvent] = useState<EventMessage>(null);
	const [kitchenState, setKitchenState] = useState(controller?.kitchen ?? null);

	const [containerSize, setContainerSize] = useState<{ width: number; height: number; }>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	// Callback ref (not useEffect) so the ResizeObserver follows the DOM node across render branches
	const setContainerRef = useCallback((node: HTMLDivElement | null): void => {
		resizeObserverRef.current?.disconnect();
		resizeObserverRef.current = null;
		if (!node) return;

		const observer = new ResizeObserver((entries) => {
			const [entry] = entries;
			if (!entry) return;
			const { width, height } = entry.contentRect;
			if (width > 0 && height > 0) {
				setContainerSize((prev) => {
					if (prev && prev.width === Math.round(width) && prev.height === Math.round(height)) return prev;
					console.log('[Kitchen3d] Container dimensions detected:', Math.round(width), 'x', Math.round(height));
					return { width: Math.round(width), height: Math.round(height) };
				});
			}
		});

		observer.observe(node);
		resizeObserverRef.current = observer;
	}, []);

	// Map layer event nouns to Three.js userData.type for visibility toggling
	const layerToObjectType: Record<string, string> = {
		'layer-option-appliance': 'appliance',
		'layer-option-cutout': 'cutout',
		'layer-option-countertop': 'countertop',
		'layer-option-corner': 'corner'
	};

	// Sync kitchen state when controller changes (e.g. switching design tabs)
	useEffect(() => {
		setKitchenState(controller?.kitchen ?? null);
	}, [controller]);

	useEffect(() => {
		if (controller) {
			const subs = controllerSubscriptions;

			// Blueprint and visibility events handled by this component
			// Material changes (surround_material, countertop_material) are handled
			// by Kitchen3dController's event-driven microtask pipeline — no action needed here.
			const rebuildNouns = [
				'blueprint',
				'visibility'
			];
			subs.push(controller.subscribe(rebuildNouns, '*', setEvent));

			// Layer visibility events — toggle Three.js object visibility directly (no rebuild)
			const layerNouns = Object.keys(layerToObjectType);
			subs.push(controller.subscribe(layerNouns, '*', (layerEvent: EventMessage) => {
				const objectType = layerToObjectType[layerEvent.noun];
				if (objectType) {
					// Layer-option events publish crud payloads carrying the toggle's old/new boolean
					controller.setLayerVisibility(objectType, (layerEvent.payload as EventPayloadCrud<boolean>).new as boolean);
				}
			}));

			// Debug cutout toggle — show/hide semi-transparent cutout shapes
			subs.push(controller.subscribe(['layer-option-debug-cutout'], '*', () => {
				controller.setDebugCutouts(controller.options.debugCutouts);
			}));

			// Controls toggle — show/hide brightness panel overlay
			subs.push(controller.subscribe(['layer-option-controls'], '*', () => {
				const show = controller.options.showControls;
				setShowControlsPanel(show);
				if (!show) {
					controller.restoreBrightness();
				}
			}));

			// Debug mode toggle — keep the tools-icon gate in sync with the actual
			// debug state (set_debug_mode publishes this after mutating options).
			subs.push(controller.subscribe(['debug_mode'], '*', () => {
				setDebugUi({
					debugMode: controller.options.debugMode ?? false,
					showTools: controller.options.debugOptions?.showTools ?? false
				});
			}));
			// Sync once now, in case debug mode was set before this subscription.
			setDebugUi({
				debugMode: controller.options.debugMode ?? false,
				showTools: controller.options.debugOptions?.showTools ?? false
			});

			// Separate subscription: texture events only trigger re-render (not rebuild)
			subs.push(controller.subscribe(['sky_key', 'floor_key', 'custom_texture'], '*', setTextureEvent));
			setControllerSubscriptions(subs);
		}

		return () => {
			if (controller) {
				controllerSubscriptions.forEach((sId) => { controller.unsubscribe(sId); });
			}
		};
	}, [controller]);

	useEffect(() => {
		if (!event || !controller) return;

		// Blueprint changed — update kitchen state to trigger re-render with new kitchen prop
		if (event.noun === 'blueprint') {
			setKitchenState(controller.kitchen);
		}
	}, [event]);

	if (controller === null) return <></>;

	return <div ref={setContainerRef} style={{
		position: 'relative',
		height: '100%',
		width: '100%',
		visibility: controller.isVisible === false ? 'hidden' : 'visible'
	}}>
		{containerSize && <Render3DKitchen
			kitchen={kitchenState}
			controller={controller}
			containerSize={containerSize}
			backgroundColor={backgroundColor}
			floorKey={controller.floorKey}
			skyKey={controller.skyKey}
			customTexture={controller.customTexture}
			renderOffset={renderOffset}
			viewportAnchor={viewportAnchor}
			imageFov={imageFov}
			canvasMinHeight={canvasMinHeight}
			instanceRef={instanceRef ?? undefined}
			ownsController={ownsController}
			arEnabled={arEnabled}
		/>}
		<BrightnessPanel
			isVisible={showControlsPanel}
			onBrightnessChange={(value) => { controller?.setBrightness(value); }}
		/>
		{controller && debugUi.debugMode && debugUi.showTools && (
			<>
				{/* Tools Panel Toggle Button */}
				<button
					onClick={() => setToolsPanelVisible(!toolsPanelVisible)}
					style={{
						position: 'absolute',
						top: '16px',
						left: '16px',
						zIndex: 1001,
						padding: '8px',
						backgroundColor: '#1976d2',
						color: 'white',
						border: 'none',
						borderRadius: '4px',
						fontSize: '16px',
						cursor: 'pointer',
						boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
						fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
					}}
					title="Toggle Tools Interface"
				>
					🔧
				</button>
				{/* Tools Panel */}
				<ToolsPanel
					controller={controller}
					visible={toolsPanelVisible}
					onClose={() => setToolsPanelVisible(false)}
				/>
			</>
		)}
	</div>;
};
